/**
 * droid_classes.ts — THE DROID CLASSES (2026-09-24): six crafted droid types and what each can do.
 *
 * MIRROR: byte-identical at `client/src/lib/droid_classes.ts` and `server/src/lib/droid_classes.ts`
 * (verify:droid-lang diffs them). Pure — no spacetimedb, no DOM.
 *
 *   battle    a bipedal combat droid: guards, patrols and fights wildlife; small cargo.
 *   supply    a four-legged hauler: the big cargo bed, extractors, storage, organising, vending.
 *   medic     a hovering medical droid: heals players, versonas and other droids.
 *   humanoid  the general-purpose droid: a little of everything, and the only one that crafts.
 *   ship      a starship technician: the active ship's hold, fittings and status; structure repair.
 *   mega      a tank-sized battle droid: slow, armoured, splash damage — and expensive to run.
 *
 * The two older items keep working: `droid_aegis` is a battle droid and `droid_ember` a supply droid.
 *
 * COST LAW: every droid draws MAINTENANCE CREDITS from its own pool — a running cost per hour while
 * it works (none while it stands idle) plus a price per action. Harder actions cost more (an attack,
 * a craft, a survey point) and longer programs cost more (the hourly draw). The class multiplies the
 * action prices (`opMult`) and sets the hourly draw; crafted quality makes a droid a little cheaper.
 */

export type DroidClass = 'battle' | 'supply' | 'medic' | 'humanoid' | 'ship' | 'mega';

/** A capability a command or query needs. */
export type DroidCap =
  | 'move' | 'scan' | 'survey' | 'cargo' | 'storage' | 'organize' | 'extract'
  | 'craft' | 'vend' | 'social' | 'attack' | 'heal' | 'repair' | 'ship';

export interface DroidClassSpec {
  cls: DroidClass;
  /** The crafted item key that deploys as this class. */
  itemKey: string;
  name: string;
  blurb: string;
  /** Walking speed (m/s) at quality 500. */
  speed: number;
  maxHp: number;
  /** Distinct cargo entries (a stack or an item each). */
  cargoSlots: number;
  /** Total resource units the cargo holds. */
  cargoUnits: number;
  /** How far its sensors reach (m). */
  scanRangeM: number;
  /** How far from its home point a program may take it (m). */
  leashM: number;
  /** Damage per strike (0 = cannot attack). */
  attackDmg: number;
  attackRangeM: number;
  /** Splash radius around the target (m, 0 = none). */
  splashM: number;
  /** Health restored per heal (0 = cannot heal). */
  healPerAct: number;
  /** Maintenance credits per hour while running. */
  runPerHour: number;
  /** Multiplier on every action's price. */
  opMult: number;
  /** Damage taken is multiplied by this (armour). */
  armor: number;
  /** Rendered size: the model's longest edge (m). */
  sizeM: number;
  /** Hover height above the ground (0 = it walks on the ground). */
  hoverM: number;
  /** At most this many of the class deployed per owner. */
  maxPerOwner: number;
  caps: ReadonlyArray<DroidCap>;
}

export const DROID_CLASSES: Record<DroidClass, DroidClassSpec> = {
  battle: {
    cls: 'battle', itemKey: 'droid_battle', name: 'Battle Droid',
    blurb: 'Guards, patrols and fights wildlife. Armoured, fast, a light cargo rack.',
    speed: 5, maxHp: 180, cargoSlots: 6, cargoUnits: 80, scanRangeM: 180, leashM: 800,
    attackDmg: 16, attackRangeM: 18, splashM: 0, healPerAct: 0,
    runPerHour: 60, opMult: 1.5, armor: 0.8, sizeM: 2.1, hoverM: 0, maxPerOwner: 3,
    caps: ['move', 'scan', 'survey', 'cargo', 'social', 'attack'],
  },
  supply: {
    cls: 'supply', itemKey: 'droid_supply', name: 'Supply Droid',
    blurb: 'A four-legged hauler: the big cargo bed, extractor runs, storage, organising and a vendor stall.',
    speed: 3.6, maxHp: 140, cargoSlots: 24, cargoUnits: 1500, scanRangeM: 150, leashM: 900,
    attackDmg: 0, attackRangeM: 0, splashM: 0, healPerAct: 0,
    runPerHour: 24, opMult: 1.0, armor: 1.0, sizeM: 2.0, hoverM: 0, maxPerOwner: 3,
    caps: ['move', 'scan', 'survey', 'cargo', 'storage', 'organize', 'extract', 'vend', 'social'],
  },
  medic: {
    cls: 'medic', itemKey: 'droid_medic', name: 'Medical Droid',
    blurb: 'Hovers at your side and heals — you, other travelers, townsfolk and other droids.',
    speed: 4.6, maxHp: 100, cargoSlots: 6, cargoUnits: 60, scanRangeM: 150, leashM: 700,
    attackDmg: 0, attackRangeM: 0, splashM: 0, healPerAct: 14,
    runPerHour: 36, opMult: 1.2, armor: 1.0, sizeM: 1.7, hoverM: 0.25, maxPerOwner: 3,
    caps: ['move', 'scan', 'cargo', 'social', 'heal', 'repair'],
  },
  humanoid: {
    cls: 'humanoid', itemKey: 'droid_humanoid', name: 'Humanoid Droid',
    blurb: 'The general-purpose droid: hands for crafting, a little of every trade, a light blaster and a first-aid kit.',
    speed: 4.2, maxHp: 120, cargoSlots: 12, cargoUnits: 400, scanRangeM: 150, leashM: 800,
    attackDmg: 7, attackRangeM: 12, splashM: 0, healPerAct: 6,
    runPerHour: 30, opMult: 1.0, armor: 1.0, sizeM: 1.8, hoverM: 0, maxPerOwner: 3,
    caps: ['move', 'scan', 'survey', 'cargo', 'storage', 'organize', 'extract', 'craft', 'vend', 'social', 'attack', 'heal', 'repair'],
  },
  ship: {
    cls: 'ship', itemKey: 'droid_ship', name: 'Ship Droid',
    blurb: 'A starship technician: loads and unloads the active ship, fits components, reports its status, repairs structures.',
    speed: 3.6, maxHp: 110, cargoSlots: 12, cargoUnits: 400, scanRangeM: 150, leashM: 700,
    attackDmg: 0, attackRangeM: 0, splashM: 0, healPerAct: 0,
    runPerHour: 36, opMult: 1.2, armor: 1.0, sizeM: 1.15, hoverM: 0, maxPerOwner: 3,
    caps: ['move', 'scan', 'cargo', 'storage', 'social', 'repair', 'ship'],
  },
  mega: {
    cls: 'mega', itemKey: 'droid_mega', name: 'Mega Battle Droid',
    blurb: 'A tank-sized war machine: slow, heavily armoured, twin cannons with splash damage. Costly to run.',
    speed: 2.6, maxHp: 1200, cargoSlots: 8, cargoUnits: 300, scanRangeM: 280, leashM: 1200,
    attackDmg: 60, attackRangeM: 36, splashM: 8, healPerAct: 0,
    runPerHour: 300, opMult: 3.0, armor: 0.45, sizeM: 8.5, hoverM: 0, maxPerOwner: 1,
    caps: ['move', 'scan', 'survey', 'cargo', 'social', 'attack'],
  },
};

export const DROID_CLASS_ORDER: ReadonlyArray<DroidClass> = ['battle', 'supply', 'medic', 'humanoid', 'ship', 'mega'];

/** Every item key that deploys as a droid (the six classes + the two older droids). */
export const DROID_ITEM_CLASS: Record<string, DroidClass> = {
  droid_battle: 'battle', droid_supply: 'supply', droid_medic: 'medic',
  droid_humanoid: 'humanoid', droid_ship: 'ship', droid_mega: 'mega',
  droid_aegis: 'battle', droid_ember: 'supply',
};

/** The class of a droid item key (null = not a droid). */
export function droidClassOf(itemKey: string): DroidClass | null {
  return DROID_ITEM_CLASS[itemKey] ?? null;
}

export function droidSpecOf(itemKey: string): DroidClassSpec | null {
  const c = droidClassOf(itemKey);
  return c ? DROID_CLASSES[c] : null;
}

/**
 * THE TERMINAL IS THE HULL (2026-09-24): hands-on work (a program, cargo, a repair, a purchase) is measured from the
 * droid's BODY — `range` plus half the class's length — not from its centre. The mega battle droid is 8.5 m long: a
 * player 12 m from its centre stood 8 m from the hull and the terminal still refused ("I can not upload and run a
 * program"). One function, both mirrors: the client's gate and the server's check can never disagree.
 */
export function droidReachM(itemKey: string, range: number): number {
  const spec = droidSpecOf(itemKey);
  return range + (spec ? spec.sizeM / 2 : 0.55);
}

export function classHas(cls: DroidClass, cap: DroidCap): boolean {
  return DROID_CLASSES[cls].caps.includes(cap);
}

/** Crafted quality (0..1000) → the stat and cost multipliers. q500 is 1.0 on every axis. */
export function qualityMults(quality: number): { hp: number; speed: number; power: number; cost: number } {
  const q = Math.max(0, Math.min(1000, quality)) / 1000;
  return {
    hp: 0.8 + 0.4 * q,
    speed: 0.9 + 0.2 * q,
    power: 0.85 + 0.3 * q,
    cost: 1.1 - 0.2 * q,
  };
}

/** The deployed stats of a droid of this class at this quality. */
export function droidStats(cls: DroidClass, quality: number): {
  maxHp: number; speed: number; attackDmg: number; healPerAct: number; runPerHour: number; opMult: number;
} {
  const s = DROID_CLASSES[cls];
  const m = qualityMults(quality);
  return {
    maxHp: Math.round(s.maxHp * m.hp),
    speed: s.speed * m.speed,
    attackDmg: Math.round(s.attackDmg * m.power),
    healPerAct: Math.round(s.healPerAct * m.power),
    runPerHour: s.runPerHour * m.cost,
    opMult: s.opMult * m.cost,
  };
}

// ── Maintenance ─────────────────────────────────────────────────────────────────────────────────

/** Credits are kept in MILLI-credits on the server (integers). */
export const MILLI = 1000;
/** A fresh droid arrives with this much charge (spent first; never refunded). */
export const STARTER_CHARGE = 10;
/** The most a pool may hold (credits). */
export const POOL_MAX = 1_000_000;
/** Moving costs this many credits per metre (before the class multiplier). */
export const MOVE_COST_PER_M = 0.004;
/** Every VM instruction and every sensor read costs a little (the droid's "thinking"). */
export const INSTR_COST = 0.001;
export const QUERY_COST = 0.02;
/** How long a droid may run a program between reports of progress (s) — the tick clamp. */
export const STEP_MAX_S = 10;

/** Hours of running a pool buys at a rate (credits/hour). */
export function runtimeHours(poolCredits: number, perHour: number): number {
  if (perHour <= 0) return Infinity;
  return Math.max(0, poolCredits) / perHour;
}

/** Credits needed to run `hours` at a rate. */
export function costForHours(hours: number, perHour: number): number {
  return Math.max(0, hours) * Math.max(0, perHour);
}

/** Money out of a pool back to the wallet: whole credits above the starter charge still unspent. */
export function refundableCredits(poolMilli: number, bonusMilli: number): number {
  return Math.max(0, Math.floor((poolMilli - Math.max(0, bonusMilli)) / MILLI));
}

// ── Upkeep: a droid pays YOUR structures from YOUR wallet (2026-09-28) ─────────────────────────

/** One `upkeep` call moves at most this many credits. */
export const UPKEEP_CALL_MAX = 500;
/** And one droid moves at most this many in any rolling hour: `forever: upkeep 500` moved 500 credits a SECOND out of
 *  the owner's wallet (bounded only by the wallet; the dearest structure drains ~140 an hour). */
export const UPKEEP_HOUR_CAP = 1000;
/** The rolling hour, kept as per-minute buckets [minuteIndex, credits] (≤ 60 of them — a bounded row). */
export type UpkeepBuckets = Array<[number, number]>;
export const upkeepMinute = (micros: bigint): number => Number(micros / 60_000_000n);

/** The buckets still inside the hour ending at `nowMinute` (malformed entries dropped). */
export function upkeepLive(buckets: UpkeepBuckets, nowMinute: number): UpkeepBuckets {
  const out: UpkeepBuckets = [];
  for (const b of Array.isArray(buckets) ? buckets : []) {
    if (!Array.isArray(b) || !Number.isFinite(b[0]) || !Number.isFinite(b[1]) || b[1] <= 0) continue;
    if (b[0] > nowMinute - 60 && b[0] <= nowMinute) out.push([b[0], b[1]]);
  }
  return out;
}
/** Credits paid in the rolling hour. */
export function upkeepPaid(buckets: UpkeepBuckets, nowMinute: number): number {
  let s = 0;
  for (const b of upkeepLive(buckets, nowMinute)) s += b[1];
  return s;
}
/** How much more this droid may pay now. */
export function upkeepRoom(buckets: UpkeepBuckets, nowMinute: number): number {
  return Math.max(0, UPKEEP_HOUR_CAP - upkeepPaid(buckets, nowMinute));
}
/** The buckets after paying `amount` now. */
export function upkeepRecord(buckets: UpkeepBuckets, nowMinute: number, amount: number): UpkeepBuckets {
  const live = upkeepLive(buckets, nowMinute);
  const last = live[live.length - 1];
  if (last && last[0] === nowMinute) last[1] += amount; else live.push([nowMinute, amount]);
  return live;
}
/** Minutes until the rolling hour frees `need` credits (0 = now). */
export function upkeepWaitMinutes(buckets: UpkeepBuckets, nowMinute: number, need: number): number {
  let room = upkeepRoom(buckets, nowMinute);
  if (room >= need) return 0;
  for (const b of upkeepLive(buckets, nowMinute).sort((a, c) => a[0] - c[0])) {
    room += b[1];
    if (room >= need) return Math.max(1, b[0] + 60 - nowMinute);
  }
  return 60;
}
